import React, { useEffect, useRef, useState } from 'react';
import { Anchor, Card, Typography, Tag, Alert, Space, Divider } from 'antd';
import { Link as RouterLink } from 'react-router-dom';
import { LabelingGuide } from '../components/LabelingGuide';
import ClassGlossary from '../components/Onboarding/classGlossary';
import { useCurrentUser } from '../hooks/useCurrentUser';

const { Title, Paragraph, Text } = Typography;

// Local <kbd>-style chip. antd doesn't ship a Kbd component.
const KeyChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '1px 6px',
      margin: '0 2px',
      background: '#fafafa',
      border: '1px solid #d9d9d9',
      borderRadius: 3,
      fontFamily: 'monospace',
      fontSize: 11,
      lineHeight: '16px',
      color: '#262626',
    }}
  >
    {children}
  </span>
);

const HelpPage: React.FC = () => {
  // Agent-facing help (Agent Health tab, rule chain internals, label capture,
  // contribution, metrics, agent FAQ) is admin-only while the LLM-retrieve
  // agent is in its testing phase — mirrors the nav gating of the Agent Health
  // tab. Operators keep the workflow help (overview, shortcuts, taxonomy, the
  // Triage/Issues/Tasks/Actions tabs, labeling guide, data sources).
  const { isAdmin } = useCurrentUser();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // antd <Anchor getContainer> is invoked once at mount. The first call
  // happens before the ref settles (returns window as fallback), so
  // scroll-spy binds to the wrong element until next render. Force a
  // re-render after the ref attaches so getContainer re-binds to the
  // actual scroll container (audit 2026-05-25).
  const [containerReady, setContainerReady] = useState(false);
  useEffect(() => {
    if (scrollRef.current) setContainerReady(true);
  }, []);
  return (
    <div
      ref={scrollRef}
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: 24,
        display: 'flex',
        gap: 24,
        justifyContent: 'center',
      }}
    >
      <Anchor
        affix={false}
        // `key` forces remount once the ref settles so getContainer
        // re-binds to the real scroll container (see useEffect above).
        key={containerReady ? 'ready' : 'mount'}
        getContainer={() => scrollRef.current || window}
        style={{ minWidth: 220, position: 'sticky', top: 0, alignSelf: 'flex-start' }}
        items={[
          { key: 'overview', href: '#overview', title: 'Overview' },
          { key: 'shortcuts', href: '#shortcuts', title: 'Keyboard shortcuts' },
          { key: 'classes', href: '#classes', title: 'Ticket-class taxonomy' },
          { key: 'triage', href: '#triage', title: 'Triage tab' },
          { key: 'issues', href: '#issues', title: 'Issues tab' },
          { key: 'tasks', href: '#tasks', title: 'Work Tasks tab' },
          { key: 'actions', href: '#actions', title: 'Actions tab' },
          ...(isAdmin
            ? [
                { key: 'agent', href: '#agent', title: 'Agent Health tab' },
                { key: 'how-agent', href: '#how-agent', title: 'How the agent works' },
                { key: 'rule-chain', href: '#rule-chain', title: 'Rule chain' },
                { key: 'label-capture', href: '#label-capture', title: 'How labels are captured' },
                { key: 'contribution', href: '#contribution', title: 'Your contribution' },
              ]
            : []),
          { key: 'labeling-guide', href: '#labeling-guide', title: 'Labeling guide' },
          ...(isAdmin
            ? [{ key: 'metrics', href: '#metrics', title: 'Reading the metrics' }]
            : []),
          { key: 'data-paths', href: '#data-paths', title: 'Data sources' },
          { key: 'faq', href: '#faq', title: 'FAQ' },
        ]}
      />

      <div style={{ flex: 1, minWidth: 0, maxWidth: 1024 }}>
        <Title level={2}>Fleet Triage Dashboard — Help</Title>
        <Paragraph type="secondary">
          {/* UX-056: derive "last updated" from build env so it doesn't go
              stale silently. VITE_BUILD_DATE is injected by the deploy
              pipeline (or falls back to "dev" in local). */}
          Last updated:{' '}
          {(import.meta.env.VITE_BUILD_DATE as string | undefined) || 'dev build'}.
          Reach out in #fleet-ops or to the dashboard maintainer if anything here is wrong.
        </Paragraph>

        {/* ---------------------------------------------------------------- */}
        <Card id="overview" title="Overview" style={{ marginBottom: 16 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="New here?"
            description={
              <span>
                Take the 30-second guided tour:{' '}
                <RouterLink to="/triage?tour=1">start the tour →</RouterLink>
              </span>
            }
          />
          <Paragraph>
            The Fleet Triage Dashboard turns the firehose of raw <code>sf_support.tickets</code> alerts into one row per real
            problem. Every open alert about the same node, gateway, or location is collapsed into a single
            <Text strong> issue</Text>; the issue is then classified into a ticket class so the right team gets it.
          </Paragraph>
          <Paragraph>There are three layers of intelligence behind the classifications you see:</Paragraph>
          <ul>
            <li>
              <Tag color="default">heuristic</Tag> — deterministic rule chain (full source in{' '}
              <Text code>server/src/services/issueClassifier.ts</Text>). Always runs first; output is the auditable
              safety floor. The classifier version is stamped on every prediction (stored in
              <Text code>issue_predictions</Text>); bump notes live in the classifier source commits.
            </li>
            <li>
              <Tag color="blue">llm-retrieve</Tag> — an LLM-with-retrieval triage agent (Claude Haiku 4.5) reading the
              same signals plus historically labeled exemplars. Today running in <Text code>SHADOW_MODE</Text> — its
              verdicts are logged but the rule's verdict is what surfaces to operators.
            </li>
            <li>
              <Tag color="default">manual</Tag> — you. Whatever you set on a close is sacred and never overwritten by
              the rule chain.
            </li>
          </ul>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="shortcuts" title="Keyboard shortcuts" style={{ marginBottom: 16 }}>
          <Paragraph>
            All shortcuts are global on <RouterLink to="/triage">Triage</RouterLink> and{' '}
            <RouterLink to="/issues">Issues</RouterLink>. They auto-suppress when an input, a dropdown, modal, or drawer is open.
          </Paragraph>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px', width: 140 }}>Key</th>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ padding: '4px 8px' }}><KeyChip>1</KeyChip>…<KeyChip>8</KeyChip></td><td style={{ padding: '4px 8px' }}>Triage actions in left-to-right order on the action bar. Hover any button to see its key. Targets the selected issue (or selected ticket on the legacy alerts tab).</td></tr>
              <tr><td style={{ padding: '4px 8px' }}><KeyChip>↑</KeyChip> / <KeyChip>↓</KeyChip></td><td style={{ padding: '4px 8px' }}>Navigate up/down the issue (or ticket) list.</td></tr>
              <tr><td style={{ padding: '4px 8px' }}><KeyChip>Ctrl</KeyChip> + <KeyChip>K</KeyChip></td><td style={{ padding: '4px 8px' }}>Open the command palette — search by location, action, filter, or jump to any tab.</td></tr>
              <tr><td style={{ padding: '4px 8px' }}><KeyChip>Shift</KeyChip> + <KeyChip>?</KeyChip></td><td style={{ padding: '4px 8px' }}>Open this shortcut cheat-sheet as a modal.</td></tr>
            </tbody>
          </table>
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
            Hotkeys target one issue at a time — the bulk-action mode was removed (2026-04). Use the command palette (<KeyChip>Ctrl</KeyChip>+<KeyChip>K</KeyChip>) for cross-location jumps.
          </Paragraph>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="classes" title="Ticket-class taxonomy" style={{ marginBottom: 16 }}>
          <Paragraph>
            The same 9 classes are used across rules, the agent, and the close
            modal. The <Text strong>Button</Text> column is the action-bar number
            (and keyboard shortcut) that applies each class on the Triage/Issues
            tabs. Class 10 has no button — it is the fallback sink, not a choice
            you assign.
          </Paragraph>
          <ClassGlossary />
          <Paragraph style={{ marginTop: 12 }}>
            Classes the rule rarely picks today (less than ~20 historical operator labels) get extra latitude from the
            agent: <Tag>57</Tag>, <Tag>58</Tag>, <Tag>60</Tag>. The agent can override the rule on these even when the
            rule is moderately confident — see § How the agent works → Ensemble.
          </Paragraph>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="triage" title={<><RouterLink to="/triage">Triage</RouterLink> tab</>} style={{ marginBottom: 16 }}>
          <Paragraph>
            New issues land here as <Text strong>suggestions</Text>. The 3-pane layout:
          </Paragraph>
          <ul>
            <li><Text strong>Locations pane</Text> (left) — locations with at least one open suggestion.</li>
            <li><Text strong>Issue list</Text> (middle) — one row per issue, with sparklines for battery/signal trends.</li>
            <li><Text strong>Detail pane</Text> (right) — node detail, alerts, classifier reasoning, suggested action.</li>
          </ul>
          <Paragraph>
            The fastest path is the suggested-action banner. <Text strong>Confirm</Text> promotes the issue to{' '}
            <RouterLink to="/issues">Issues</RouterLink> and (when applicable) opens a task-creation modal pre-filled
            with the right action and executor.
          </Paragraph>
          <Paragraph>
            The <Text strong>Why this class</Text> callout leads with an <Text strong>evidence strip</Text> — the concrete signals that drove the class, as chips (e.g. <Tag style={{ margin: 0 }}>offline 42d</Tag> <Tag style={{ margin: 0 }}>120mAh excess draw</Tag> <Tag style={{ margin: 0 }}>-92 dBm</Tag> <Tag style={{ margin: 0 }}>6 on gateway</Tag>) — with the rule's one-line rationale beneath. Scan the chips, then read the sentence if you need more.
          </Paragraph>
          <Paragraph>
            The <Text strong>Classifier reasoning</Text> section (collapsible) shows the rule that matched, the class it points to, and the criteria it checks. The one-line action recipe lives in the <Text strong>Recommended next step</Text> banner near the top of the pane. Below, the <Text strong>Agent prediction</Text> card shows the LLM-retrieve verdict if any.
          </Paragraph>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="issues" title={<><RouterLink to="/issues">Issues</RouterLink> tab</>} style={{ marginBottom: 16 }}>
          <Paragraph>
            Confirmed issues that are still open. Same layout as Triage, but the action bar is full triage:
          </Paragraph>
          <ul>
            <li><Text strong>Re-classify</Text> — re-runs the heuristic chain against current signals. Skips manually-pinned issues unless you pass <Text code>force</Text>.</li>
            <li><Text strong>Triage actions</Text> — set ticket_class + action + root_cause and either close or move to a non-terminal status. Setting any of these flips classifier_version to <Text code>manual</Text>; the rule chain will not overwrite it.</li>
          </ul>
          <Alert
            type="info"
            showIcon
            message="Closing an issue here is also a labeling event."
            description={<>Each close writes a row to <Text code>sf_support.issue_outcomes</Text> with <Text code>decision_source='operator'</Text> and the class/action/root_cause you set. See § How labels are captured for the full schema. These rows are the agent's training set.</>}
            style={{ marginTop: 8 }}
          />

          <Title level={5} style={{ marginTop: 12 }}>Agent prediction card</Title>
          <Paragraph>
            Every issue that's been seen by the LLM-retrieve sidecar gets an "Agent prediction" card right under the
            Classifier reasoning section. It shows:
          </Paragraph>
          <ul>
            <li>Agent's class + confidence.</li>
            <li>A single chip: <Tag color="green">agrees</Tag> when the agent's class matches the rule classifier, or <Tag color="gold">disagrees</Tag> when it would pick another class — that's the one signal worth acting on.</li>
            <li>A one-line reason from the LLM.</li>
            <li>Other guesses (alternatives + confidence), labeled with class names not raw IDs.</li>
          </ul>
          <Paragraph>
            The card is silent on issues the agent hasn't yet seen. The deeper model mechanics —
            which ensemble arm decided, the 5% hold-out, and the class-10 promotion gate — are
            debugging detail and live on the <RouterLink to="/agent-health">Agent Health</RouterLink> page,
            not in this operator view.
          </Paragraph>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="tasks" title={<><RouterLink to="/tasks">Work Tasks</RouterLink> tab</>} style={{ marginBottom: 16 }}>
          <Paragraph>
            Per-location task board. Each task has a status, executor, due date, and a parent (issue or ticket). The
            Excel export at the top of the page lets you hand a punch list to a contractor; it includes a "Nodes
            Needed" sheet for replacement-node tasks.
          </Paragraph>
          <Paragraph>
            The status filter at the top includes Pending, In Progress, Accepted, Done, Canceled. Per-row Select uses
            the same values; the table cell color matches across both surfaces via the central{' '}
            <Text code>taskStatusBadgeMap</Text>.
          </Paragraph>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="actions" title={<><RouterLink to="/actions">Actions</RouterLink> tab</>} style={{ marginBottom: 16 }}>
          <Paragraph>
            Maintenance catalog — the canonical set of <Text code>sf_support.actions</Text> rows the dashboard uses for
            triage and task creation. Edit here once and it propagates to the suggestion banner.
          </Paragraph>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Delete requires the <Text code>admin</Text> or <Text code>fleet-ops</Text> Auth0 role. Operators without that
            role will see "Delete requires the admin or fleet-ops role." on attempted deletes.
          </Paragraph>
        </Card>

        {isAdmin && (
          <>
        {/* ---------------------------------------------------------------- */}
        <Card id="agent" title={<><RouterLink to="/agent-health">Agent Health</RouterLink> tab</>} style={{ marginBottom: 16 }}>
          <Paragraph>
            Read-only dashboard for the LLM-retrieve triage agent. Window selector top-right (7 / 30 / 90 days).
          </Paragraph>

          <Title level={5}>Top-row cards</Title>
          <ul>
            <li><Text strong>Predictions</Text> — total agent verdicts logged in window.</li>
            <li><Text strong>Operator-labeled</Text> — predictions for issues that were closed by an operator (<Text code>decision_source='operator'</Text>).</li>
            <li><Text strong>Agreement</Text> — % of operator-labeled predictions where the agent matched the operator's class.</li>
            <li><Text strong>Hold-out agreement</Text> — same metric but only on the deterministic 5% hold-out where the agent's suggestion was hidden from the operator. <Text strong>The only un-biased accuracy signal</Text> — operators can't drift toward "agree because the agent agrees" if they never saw the agent.</li>
            <li><Text strong>Avg confidence</Text> — mean self-reported confidence on agent verdicts (0–100, shown as a percent).</li>
            <li><Text strong>Brier (top-class)</Text> — calibration metric on the labeled subset. Lower is better. 0.25 = random; under 0.10 = well-calibrated. "Top-class" because the server scores the confidence of the predicted class only, not full multi-class Brier — read it as "is the confidence number trustworthy" rather than as a global calibration score.</li>
            <li><Text strong>Time-to-triage (p50)</Text> — median ms from when the operator first saw the issue to when they triaged it. Secondary line shows p95 and the close count for the latest day.</li>
          </ul>

          <Title level={5}>Confusion matrix (operator-labeled)</Title>
          <Paragraph>
            Heatmap with operator's label (truth) on the x-axis and agent's prediction on the y-axis. Diagonal = correct.
            Hover any cell for the count. Empty until operator labels accumulate.
          </Paragraph>

          <Title level={5}>Calibration (confidence vs. agreement)</Title>
          <Paragraph>
            Predictions are bucketed in 10% confidence bins. The dashed line is the ideal: a well-calibrated model
            scoring 70% should be right 70% of the time. The bars (right axis) show how many predictions are in each
            bucket. Bars far from the dashed line = miscalibrated.
          </Paragraph>

          <Title level={5}>Daily class mix + WoW movers</Title>
          <Paragraph>
            Stacked area of predictions per class per day. Sudden swings in mix usually mean an upstream data change
            (sensor batch, OTA campaign, sensorflow_analytics feed lag) rather than a real fleet shift. The "Top week-over-week
            movers" panel beside it quantifies the change — top-5 classes by absolute delta with the percentage change
            and the raw last-week → this-week counts.
          </Paragraph>

          <Title level={5}>Active-learning queue (with "Why" column)</Title>
          <Paragraph>
            Top open issues where the agent's verdict is most useful to label. The "Why" column tags each row:
          </Paragraph>
          <ul>
            <li><Tag color="gold">disagrees</Tag> — agent picked a different class than the rule. The most informative label you can provide.</li>
            <li><Tag color="orange">class-10 lock</Tag> — rule stuck on OFFLINE_UNKNOWN; agent proposed a specific class but hasn't cleared the promotion bar (needs high confidence + corroborating operator-labeled exemplars). Your label resolves the ambiguity and builds the exemplars that let the agent promote next time.</li>
            <li><Tag>low confidence</Tag> — fallback ordering when there are no current disagreements; agent's least confident open predictions are surfaced.</li>
          </ul>
          <Paragraph>Click the chevron on a row to see the agent's other guesses and the prediction timestamp.</Paragraph>

          <Title level={5}>Your contribution banner</Title>
          <Paragraph>
            At the top of the page, when you have non-zero closes: a tally of your operator labels today / this week /
            total / distinct classes. When you have zero, a softer call-to-action explains that closing issues with a
            class set populates the agent's training set. See § Your contribution.
          </Paragraph>

          <Title level={5}>sensorflow_analytics freshness alert</Title>
          <Paragraph>
            <Text code>sensorflow_analytics.v_node_offline_investigation</Text> aggregates the heavy-duty offline-investigation features
            (failure_mode, repeat_offender, dropout_pattern, etc.) on the analytics cluster. The rule classifier reads
            from it. If the latest <Text code>offline_since_day</Text> is more than 3 days stale we error — treat the
            error as <Text strong>"don't trust today's predictions"</Text> until upstream is fixed.
          </Paragraph>

          <Title level={5}>Admin: Seed agent & Embed backfill</Title>
          <Paragraph>
            <Text strong>Seed agent (50)</Text> runs the heuristic chain + agent classification on the 50 most recently
            classified open issues — useful for warming the dashboard before natural traffic accrues. Roughly $0.02–0.05
            per click. <Text strong>Embed-backfill labeled (200)</Text> seeds the kNN exemplar pool by embedding 200
            historical labeled issues — roughly $0.002 per click. Both wrapped in Popconfirm with cost estimate.
          </Paragraph>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="how-agent" title="How the agent works" style={{ marginBottom: 16 }}>
          <Paragraph>
            For every new issue, the Node sweeper does this once and the sidecar fires fire-and-forget:
          </Paragraph>
          <ol>
            <li><Text strong>Signal collection</Text> (Node) — pulls all relevant evidence: child alerts, gateway status, location cohort, OTA history, sensorflow_analytics dimensions, last-seen, signal/battery sparklines.</li>
            <li><Text strong>Rule verdict</Text> (Node) — the heuristic chain produces a <code>{'{class_id, confidence, notes}'}</code> tuple. This is the auditable baseline. See § Rule chain for the rule order.</li>
            <li><Text strong>Embed signals</Text> (sidecar) — the structured signal JSON is hashed and embedded with <Text code>text-embedding-3-small</Text> (1024 dim, L2-normalized).</li>
            <li><Text strong>kNN exemplars</Text> (sidecar) — top-5 most similar <Text strong>closed</Text> issues that already have an operator label; pgvector HNSW index on cosine distance. The query issue itself is excluded from retrieval.</li>
            <li><Text strong>LLM classification</Text> (sidecar) — Claude Haiku 4.5 receives the 9-class taxonomy, the retrieved exemplars, the rule verdict, and the new issue's signals; emits <code>{'{primary_class_id, confidence, alternatives, reasoning}'}</code>. If parsing fails or the model returns an unknown class, the sidecar falls back to the rule verdict with confidence 0.</li>
            <li>
              <Text strong>Ensemble decision</Text> (sidecar) — recorded in <Text code>decided_by</Text>:
              <ul>
                <li>If rule says class 10, agent <Text strong>cannot</Text> override (no class-10 splitting). decided_by = rule.</li>
                <li>If rule chose a non-cold class but agent picked cold (57/58/60) with conf ≥ 70 AND ≥ rule's conf, agent wins. decided_by = llm.</li>
                <li>If rule conf &lt; 60 AND agent conf ≥ rule's conf, agent wins. decided_by = llm.</li>
                <li>If agent disagrees with rule with conf ≥ 70 AND ≥ rule's conf, agent wins. decided_by = llm.</li>
                <li>Otherwise rule alone. decided_by = rule.</li>
              </ul>
            </li>
            <li><Text strong>5% hold-out</Text> — <code>hash(issue_id) mod 100 &lt; 5</code> using md5 for uniform sampling. Hold-out predictions are recorded but never shown to operators. Used to compute the only un-biased accuracy metric.</li>
            <li><Text strong>Persist</Text> — embedding (cached by signals_hash), prediction (append-only into <Text code>issue_predictions</Text>). The persisted row records the SURFACED verdict in <Text code>predicted_class_id</Text> + <Text code>confidence</Text>, and the raw LLM verdict separately in <Text code>llm_class_id</Text> + <Text code>llm_confidence</Text>.</li>
          </ol>
          <Alert
            type="warning"
            showIcon
            message="The agent is in SHADOW_MODE today."
            description="Every step above runs end-to-end, but step 6 returns the rule verdict regardless. Predictions land in the database with decided_by='rule'; nothing surfaces to operators yet. We flip out of shadow mode only when hold-out agreement clears 70% over at least 100 operator labels."
          />
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="rule-chain" title="Rule chain" style={{ marginBottom: 16 }}>
          <Paragraph>
            The rule classifier matches in priority order — first matching rule wins. Full source:{' '}
            <Text code>server/src/services/issueClassifier.ts</Text>. v4 (2026-05-18) added or fixed:
          </Paragraph>
          <ul>
            <li>
              <Text strong>R-CHILD-CLASS</Text> (new) — when a child ticket has already been classified as
              BATTERY_DRAINED, TAMPERING, HARDWARE_FAULT, FIRMWARE_ISSUE, or OUT_OF_RANGE, promote the parent issue to
              the same class. Closed the ~990-issue gap where class-10 was surfaced for open issues whose child alert
              already identified the cause.
            </li>
            <li>
              <Text strong>R-OTA-FAIL</Text> / <Text strong>R-OTA-WINDOW</Text> timing fix — previously required offline
              to start AFTER OTA end-time, which silently rejected the strongest firmware-brick case (offline DURING
              the OTA). Now checks offline freshness within the OTA window.
            </li>
            <li>
              <Text strong>R-OTA-WINDOW case-insensitive</Text> — legacy <Text code>ota_runs.result</Text> rows with
              "Success"/"SUCCESS" now match alongside "success".
            </li>
            <li>
              <Text strong>R-COHORT scope</Text> — cohort_size now counts DISTINCT nodes at the location (excluding the
              subject), not all tickets. A hotel with 5 unrelated noise tickets no longer flips every new node into
              cohort.
            </li>
          </ul>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="label-capture" title="How labels are captured" style={{ marginBottom: 16 }}>
          <Paragraph>
            The agent learns from exactly three sources today, and only one is reliable:
          </Paragraph>
          <Title level={5}>Operator labels (gold)</Title>
          <Paragraph>
            When you close an issue, the close call writes a row to <Text code>sf_support.issue_outcomes</Text>:
          </Paragraph>
          <ul>
            <li><Text code>ground_truth_class_id</Text> — the class you set.</li>
            <li><Text code>ground_truth_action_id</Text> / <Text code>ground_truth_root_cause_id</Text> — same.</li>
            <li><Text code>decision_source</Text> = <Text code>operator</Text>.</li>
            <li><Text code>accepted_suggestion</Text> — true if your final class matched the <Text strong>rule's</Text> verdict at modal-open.</li>
            <li><Text code>suggested_class_id</Text> — what the rule was saying when you opened the issue.</li>
            <li><Text code>agent_class_id</Text> — the latest LLM-retrieve verdict at close time (looked up server-side, not from the client).</li>
            <li><Text code>agent_match</Text> — true if the agent's verdict equaled your final class. Independent of <Text code>accepted_suggestion</Text> so we can compare rule and agent quality cleanly even after the agent surfaces verdicts.</li>
            <li><Text code>decision_latency_ms</Text> — how long the panel was open (first-seen sticky across re-selection).</li>
            <li><Text code>labeled_by</Text> — your Auth0 email.</li>
          </ul>
          <Paragraph>
            Re-closing an existing issue overwrites the outcome (newest decision wins). These are the only labels used
            in the kNN retrieval and in agreement/calibration metrics.
          </Paragraph>

          <Title level={5}>Auto labels (system)</Title>
          <Paragraph>
            When all linked alerts on an open issue resolve, the scheduler auto-closes the issue and writes an{' '}
            <Text code>issue_outcomes</Text> row with <Text code>decision_source='auto'</Text> and{' '}
            <Text code>labeled_by='system:auto-close'</Text>. These don't enter retrieval or accuracy metrics — they
            exist so total volume in the daily aggregator reflects reality.
          </Paragraph>

          <Title level={5}>Inferred labels (silver, held in reserve)</Title>
          <Paragraph>
            On 2026-05-06 we backfilled 1,107 inferred labels from the heuristic verdict on closed issues over the last
            12 months (<Text code>decision_source='inferred'</Text>). These are <Text strong>not</Text> used in
            retrieval — using them would teach the agent to mimic the heuristic. They're held in reserve for Phase 3
            Snorkel weak supervision.
          </Paragraph>
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="contribution" title="Your contribution" style={{ marginBottom: 16 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Full step-by-step labeling guide is one section down."
            description={
              <Typography.Link href="#labeling-guide">Jump to § Labeling guide ↓</Typography.Link>
            }
          />
          <Paragraph>
            Every close you do with a class set populates the agent's training set. The{' '}
            <RouterLink to="/agent-health">Agent Health</RouterLink> page shows your personal tally — closes today,
            this week, total, and the number of distinct classes you've labeled. It's read from{' '}
            <Text code>GET /api/triage-agent/my-contribution</Text>, scoped to your Auth0 email (falls back to{' '}
            <Text code>sub</Text> if email isn't set).
          </Paragraph>
          <Paragraph>
            What moves the needle fastest, in order:
          </Paragraph>
          <ol>
            <li>Close issues from the <Text strong>Active Learning Queue</Text> on Agent Health — those are the ones the agent disagrees with the rule on, so your label resolves the most ambiguity per click.</li>
            <li>Close issues across diverse classes — particularly the cold ones (57, 58, 60) which the rule never picks on its own.</li>
            <li>Re-close issues you previously triaged when the agent's verdict has changed — overwrite is allowed, newest wins.</li>
          </ol>
        </Card>
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        <Card id="labeling-guide" title="Labeling guide" style={{ marginBottom: 16 }}>
          <Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 12 }}>
            Same content available as a modal from any triage page (top-nav "Labeling guide" button).
            The Slack-pasteable Markdown version lives at <Text code>docs/OPS_LABELING_GUIDE.md</Text>.
          </Paragraph>
          <LabelingGuide />
        </Card>

        {isAdmin && (
          <>
        {/* ---------------------------------------------------------------- */}
        <Card id="metrics" title="Reading the metrics" style={{ marginBottom: 16 }}>
          <Title level={5}>Hold-out agreement &gt; everything</Title>
          <Paragraph>
            Watch the <Text strong>hold-out</Text> number more than the headline agreement number. Agreement on
            non-hold-out items is contaminated: operators slowly learn to defer to the agent, so agreement creeps up
            over time even if the agent isn't actually getting smarter. Hold-out is the reality check.
          </Paragraph>
          <Title level={5}>Brier (top-class)</Title>
          <Paragraph>
            Server computes <Text code>mean((confidence/100 - is_correct)²)</Text> on the labeled subset. Tells you
            whether the confidence number is meaningful. Brier &gt; 0.20 means you should ignore the agent's confidence.
            Brier &lt; 0.10 means the confidence is decision-grade.
          </Paragraph>
          <Title level={5}>Active-learning queue</Title>
          <Paragraph>
            Treat each row as a question the agent is asking you. The labels you provide on these specific issues will
            move the agent's accuracy faster than any other action — they're the ones where the agent thinks it knows
            something the rule doesn't.
          </Paragraph>
          <Title level={5}>Time-to-triage</Title>
          <Paragraph>
            Sourced from <Text code>issue_outcomes.decision_latency_ms</Text> (operator-labeled subset only). p50 is
            the typical close time; p95 is the tail that hides under the median. The first-seen clock is sticky per
            issue across re-selection / list refetch / navigation, so the values aren't inflated by background polling.
          </Paragraph>
          <Title level={5}>sensorflow_analytics freshness</Title>
          <Paragraph>
            Acts as a kill-switch indicator for the rule classifier. When stale, expect the rule to fall back to weak
            heuristic-only paths and the agent to surface much more often. That's not a sign the agent is "winning"
            — it's a sign the rule is degraded.
          </Paragraph>
        </Card>

          </>
        )}

        {/* ---------------------------------------------------------------- */}
        <Card id="data-paths" title="Data sources" style={{ marginBottom: 16 }}>
          <Paragraph>The dashboard reads from three logical layers:</Paragraph>
          <ul>
            <li>
              <Text strong>Production cluster</Text> (Aiven) — <Text code>sf_support.*</Text> (tickets, issues, classes,
              actions, root_causes), <Text code>sensorflow.*</Text> (nodes, locations, tasks, gateways). Read/write via
              the <Text code>analytics_loader</Text> role.
            </li>
            <li>
              <Text strong>Analytics cluster</Text> (Timescale cloud) — <Text code>sensorflow_analytics.v_node_offline_investigation</Text>,{' '}
              <Text code>sensorflow_analytics.*</Text>. Read-only.
            </li>
            {isAdmin && (
              <li>
                <Text strong>Agent state</Text> (production cluster) — <Text code>sf_support.issue_predictions</Text>,{' '}
                <Text code>issue_outcomes</Text>, <Text code>issue_embeddings</Text> (pgvector HNSW),{' '}
                <Text code>model_versions</Text>, <Text code>model_metrics_daily</Text>.
              </li>
            )}
          </ul>
          {isAdmin && (
            <Paragraph>
              The agent sidecar is a separate Python service on ECS Fargate. It only reads from the production cluster
              (via the same role) and the LLM/embedding APIs (Anthropic, OpenAI). It never reads sensorflow_analytics — by the time
              signals reach it, the Node service has already pulled them.
            </Paragraph>
          )}
        </Card>

        {/* ---------------------------------------------------------------- */}
        <Card id="faq" title="FAQ" style={{ marginBottom: 16 }}>
          {isAdmin && (
            <>
          <Title level={5}>Why doesn't the agent ever pick class 10?</Title>
          <Paragraph>
            It does pick it, but it's forbidden from <Text strong>overriding</Text> a rule-class-10 verdict. Class 10 is
            the explicit "we don't know" sink — splitting it on weak signal would move errors from "honest unknown"
            to "confidently wrong." When this triggers, the issue surfaces on the{' '}
            <RouterLink to="/agent-health">Agent Health</RouterLink> active-learning queue with the orange{' '}
            <Tag color="orange">class-10 lock</Tag> tag (it's no longer shown on the operator's Agent prediction card).
          </Paragraph>

          <Title level={5}>Why is the dashboard empty?</Title>
          <Paragraph>
            Predictions only land when new orphan observables go through the Node sweeper, or via the seed button.
            The active-learning queue requires both an open issue and an agent-rule disagreement (or low confidence
            as fallback). The confusion matrix and calibration plot require operator labels — close some issues to
            populate them.
          </Paragraph>

          <Title level={5}>How much does the agent cost to run?</Title>
          <Paragraph>
            Roughly $0.003 per /classify call (Haiku 4.5 + a small embedding). At current sweep volume (~50 new
            issues/day) that's about $5/month for LLM calls plus the ECS Fargate task. Embeddings are cached by
            signals_hash so re-classifications on unchanged signals are free.
          </Paragraph>

          <Title level={5}>What's the deal with the "via rule" / "via llm" tags?</Title>
          <Paragraph>
            They're the <Text code>decided_by</Text> column on the prediction row, showing which arm of the ensemble
            won. "via rule" is the auditable safe default; "via llm" means the rule was weak enough or the LLM
            confident enough that the agent's verdict surfaced.
          </Paragraph>

          <Title level={5}>How do I retire / re-train the agent?</Title>
          <Paragraph>
            Two-step: bump <Text code>LLM_VERSION_TAG</Text> in <Text code>predictor.py</Text> to mint a new{' '}
            <Text code>model_versions</Text> row, then deploy. New predictions reference the new version; old metrics
            stay attributed to the old version so you can A/B in the dashboard. Phase 3 (Snorkel + LightGBM) will add a
            separate trained-model path with its own versioning.
          </Paragraph>
            </>
          )}

          <Title level={5}>Where do I report a bug?</Title>
          <Paragraph>
            #fleet-ops on Slack or DM the dashboard maintainer. If it's a wrong agent prediction specifically, please
            close the issue with the correct class first — that's the signal that trains the agent.
          </Paragraph>
        </Card>

        <Divider />
        <Paragraph type="secondary">
          Plan reference: {isAdmin && (<><Text code>TRIAGE_AGENT_PLAN.md</Text>, </>)}<Text code>AUDIT_PLAN.md</Text>,{' '}
          <Text code>UX_PUNCH_LIST.md</Text> in the repo. Code references:{' '}
          <Text code>server/src/services/issueClassifier.ts</Text> (rule classifier)
          {isAdmin && (
            <>
              ,{' '}
              <Text code>triage-agent/app/predictor.py</Text> (sidecar orchestration),{' '}
              <Text code>triage-agent/app/llm_classifier.py</Text> (taxonomy + LLM prompt)
            </>
          )}.
        </Paragraph>
        <Space />
      </div>
    </div>
  );
};

export default HelpPage;
